import { useAuth } from "@contexts/auth";
import { useEffect, useState } from "react";
import {
  Button,
  Group,
  Input,
  Modal,
  Radio,
  Select,
  Stack,
  TagsInput,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { isAxiosError } from "axios";

import { CustomPhoneInput } from "@components/CustomPhoneInput";

import {
  useCreateProspect,
  useUpdateProspect,
} from "@hooks/queries/accountManagement/useAccountManagement";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import {
  Prospect,
  ProspectDraft,
} from "@customTypes/accountManagement";


const emptyDraft: ProspectDraft = {
  account_type: "doctor",
  name: "",
  specialty_or_department: "",
  email: "",
  phone_number: "",
  whatsapp_number: "",
  social_handle: "",
  lead_source: "",
  onboarding_method: "self_onboard",
  qualification_tags: [],
  notes: "",
};

interface Props {
  opened: boolean;
  onClose: () => void;
  prospect?: Prospect | null;
}

export const ProspectFormModal = ({ opened, onClose, prospect }: Props) => {
  const { can } = useAuth();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<ProspectDraft>(emptyDraft);
  const [isPhoneInputError, setIsPhoneInputError] = useState(false);
  const [isWhatsAppInputError, setIsWhatsAppInputError] = useState(false);
  const createProspect = useCreateProspect();
  const updateProspect = useUpdateProspect();
  const { data: admins } = useGetUsers({ role: "admin", isEnabled: opened && can("access.manage") });

  useEffect(() => {
    if (!opened) return;
    setStep(0);
    setIsPhoneInputError(false);
    setIsWhatsAppInputError(false);
    setDraft(
      prospect
        ? {
            account_type: prospect.account_type,
            name: prospect.name || "",
            specialty_or_department:
              prospect.specialty_or_department || "",
            email: prospect.email || "",
            phone_number: prospect.phone_number || "",
            whatsapp_number: prospect.whatsapp_number || "",
            social_handle: prospect.social_handle || "",
            lead_source: prospect.lead_source,
            onboarding_method: prospect.onboarding_method,
            qualification_tags: prospect.qualification_tags || [],
            notes: prospect.notes || "",
            identified_by: prospect.identified_by,
          }
        : emptyDraft
    );
  }, [opened, prospect]);

  const change = <K extends keyof ProspectDraft>(
    key: K,
    value: ProspectDraft[K]
  ) => setDraft(current => ({ ...current, [key]: value }));

  const firstStepValid =
    (!draft.name || draft.name.trim().length >= 2) &&
    (!draft.phone_number || draft.phone_number.trim().length >= 5) &&
    !isPhoneInputError &&
    !isWhatsAppInputError;
  const secondStepValid = draft.lead_source.trim().length >= 2;

  const save = () => {
    const payload = Object.fromEntries(
      Object.entries(draft).filter(([, value]) => value !== "")
    ) as ProspectDraft;
    if (!can("access.manage")) delete payload.identified_by;
    const mutation = prospect
      ? updateProspect.mutateAsync({
          prospectId: prospect._id,
          payload,
        })
      : createProspect.mutateAsync(payload);

    mutation
      .then(() => {
        notifications.show({
          color: "green",
          message: prospect ? "Prospect updated" : "Prospect added",
        });
        onClose();
      })
      .catch(error => {
        const detail = isAxiosError(error)
          ? error.response?.data?.detail
          : null;
        if (
          detail &&
          typeof detail === "object" &&
          detail.code === "prospect_onboarding_identity_locked"
        ) {
          notifications.show({
            color: "orange",
            title: "Onboarding identity is locked",
            message:
              "This prospect has already started onboarding. Update contact or account details from the onboarding session or linked account instead.",
          });
          return;
        }
        if (
          detail &&
          typeof detail === "object" &&
          detail.code === "prospect_onboarding_identifier_in_use"
        ) {
          notifications.show({
            color: "red",
            title: "Contact already in use",
            message:
              "Another active onboarding invitation already uses this email address or phone number.",
          });
          return;
        }
        notifications.show({
          color: "red",
          message: "The prospect could not be saved.",
        });
      });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={prospect ? "Edit Prospect" : "Add Prospect"}
      size="lg"
      centered>
      {step === 0 && (
        <Stack>
          <Radio.Group
            label="Account type"
            value={draft.account_type}
            onChange={value =>
              change("account_type", value as ProspectDraft["account_type"])
            }>
            <Group mt="xs">
              <Radio value="doctor" label="Doctor" />
              <Radio value="organization" label="Organization" />
            </Group>
          </Radio.Group>
          <TextInput
            label={
              draft.account_type === "doctor"
                ? "Doctor name"
                : "Organization name"
            }
            placeholder={
              draft.account_type === "doctor"
                ? "e.g. Jane Doe"
                : "e.g. Ridge Medical Centre"
            }
            value={draft.name}
            onChange={event => change("name", event.currentTarget.value)}
          />
          <TextInput
            label={
              draft.account_type === "doctor"
                ? "Specialty"
                : "Department or organization type"
            }
            value={draft.specialty_or_department}
            onChange={event =>
              change("specialty_or_department", event.currentTarget.value)
            }
          />
          <Group grow align="start">
            <Input.Wrapper label="Phone number">
              <CustomPhoneInput
                id="prospect-phone-number"
                phone={draft.phone_number}
                setPhone={value => change("phone_number", value)}
                setIsError={setIsPhoneInputError}
              />
            </Input.Wrapper>
            <Input.Wrapper label="WhatsApp number">
              <CustomPhoneInput
                id="prospect-whatsapp-number"
                phone={draft.whatsapp_number}
                setPhone={value => change("whatsapp_number", value)}
                setIsError={setIsWhatsAppInputError}
              />
            </Input.Wrapper>
          </Group>
          <TextInput
            type="email"
            label="Email"
            value={draft.email}
            onChange={event => change("email", event.currentTarget.value)}
          />
          <TextInput
            label="Social media handle"
            value={draft.social_handle}
            onChange={event =>
              change("social_handle", event.currentTarget.value)
            }
          />
        </Stack>
      )}

      {step === 1 && (
        <Stack>
          <Select
            required
            searchable
            label="Lead source"
            placeholder="Select a source"
            data={[
              "Doctor referral",
              "Patient referral",
              "Social media",
              "Event",
              "Website",
              "Outbound",
              "Other",
            ]}
            value={draft.lead_source}
            onChange={value => change("lead_source", value || "")}
          />
          <Select
            label="Identified by"
            searchable
            clearable
            placeholder="Current administrator"
            data={(admins || []).map(admin => ({
              value: admin.id,
              label: admin.fullName,
            }))}
            disabled={!can("access.manage")} value={draft.identified_by || null}
            onChange={value =>
              change("identified_by", value || undefined)
            }
          />
          <Select
            label="Onboarding method"
            data={[{ value: "self_onboard", label: "Self-onboard" }]}
            value={draft.onboarding_method}
            disabled
          />
        </Stack>
      )}

      {step === 2 && (
        <Stack>
          <TagsInput
            label="Qualification tags"
            placeholder="Type a tag and press Enter"
            data={[
              "Masters candidate",
              "Marketplace candidate",
              "Priority specialty",
            ]}
            value={draft.qualification_tags}
            onChange={value => change("qualification_tags", value)}
          />
          <Textarea
            label="Notes"
            minRows={5}
            maxLength={5000}
            value={draft.notes}
            onChange={event => change("notes", event.currentTarget.value)}
          />
        </Stack>
      )}

      <Group justify="space-between" mt="xl">
        <Button
          variant="default"
          disabled={step === 0}
          onClick={() => setStep(current => current - 1)}>
          Previous
        </Button>
        {step < 2 ? (
          <Button
            disabled={
              (step === 0 && !firstStepValid) ||
              (step === 1 && !secondStepValid)
            }
            onClick={() => setStep(current => current + 1)}>
            Next
          </Button>
        ) : (
          <Button
            loading={createProspect.isLoading || updateProspect.isLoading}
            onClick={save}>
            Save
          </Button>
        )}
      </Group>
    </Modal>
  );
};
